/**
 * Verdict presentation.
 *
 * The copy comes from the shared package so no panel can invent a friendlier
 * label for a result that has not been measured, and the interval bar is shown
 * next to every hit rate so a wide interval is visible rather than inferable.
 */
import { VERDICT_COPY, type VerdictDetail, formatPct } from "@tt/shared";

export function VerdictBadge({ detail }: { detail: VerdictDetail }) {
  const copy = VERDICT_COPY[detail.verdict];
  return (
    <span className={`badge ${copy.tone}`} title={copy.blurb}>
      {copy.label}
    </span>
  );
}

export function ConfidenceBadge({ samples, confidence }: { samples: number; confidence: string }) {
  const tone =
    confidence === "good" ? "good" :
    confidence === "moderate" ? "" :
    confidence === "low" ? "warn" : "bad";
  return (
    <span
      className={`badge ${tone}`}
      title={
        `${samples} independent observations after removing overlapping forward ` +
        `windows. Below about 30, a hit rate says almost nothing.`
      }
    >
      n={samples}
    </span>
  );
}

/**
 * A hit rate drawn as its confidence interval, with the baseline marked.
 *
 * The point of the picture: a 62% hit rate whose interval spans 40-80% and a
 * 62% hit rate whose interval spans 59-65% are completely different claims,
 * and a bare "62%" hides that.
 */
export function IntervalBar({
  low, high, point, baseline, min = 0.3, max = 0.8,
}: {
  low: number; high: number; point: number;
  baseline?: number | null; min?: number; max?: number;
}) {
  const span = max - min;
  const clamp = (v: number) => Math.max(0, Math.min(100, ((v - min) / span) * 100));
  const l = clamp(low);
  const h = clamp(high);
  const p = clamp(point);
  const b = baseline !== null && baseline !== undefined ? clamp(baseline) : null;
  return (
    <span
      className="interval"
      title={
        `95% interval ${formatPct(low)} to ${formatPct(high)}` +
        (b !== null ? `, baseline ${formatPct(baseline as number)}` : "")
      }
    >
      <span className="band" style={{ left: `${l}%`, width: `${Math.max(1, h - l)}%` }} />
      <span className="point" style={{ left: `${p}%` }} />
      {b !== null && <span className="base" style={{ left: `${b}%` }} />}
    </span>
  );
}
